import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/site/Logo";
import { useAuth } from "@/lib/auth";
import { Lock, User as UserIcon } from "lucide-react";

export default function AdminLogin() {
    const nav = useNavigate();
    const { user, login } = useAuth();
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    if (user) return <Navigate to="/admin" replace />;

    const submit = async (e) => {
        e.preventDefault();
        if (!username || !password) return toast.error("Enter both fields");
        setBusy(true);
        try {
            await login(username, password);
            toast.success("Welcome back");
            nav("/admin");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Login failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="grid min-h-screen bg-gradient-to-br from-brand-blue-light via-white to-slate-50 lg:grid-cols-2">
            <div className="hidden flex-col justify-between p-12 lg:flex">
                <div className="flex items-center gap-3">
                    <Logo size={44} />
                    <div>
                        <div className="font-serif text-lg font-semibold text-brand-blue">Gaurav Sankalp</div>
                        <div className="text-[10px] font-medium uppercase tracking-[0.22em] text-slate-500">Phase-1 Association</div>
                    </div>
                </div>
                <div>
                    <div className="overline">Admin Panel</div>
                    <h1 className="mt-4 font-serif text-5xl leading-tight text-slate-900">Manage every corner of the association.</h1>
                    <p className="mt-4 max-w-md text-slate-600">Control societies, committee, documents, gallery, events, news, contact submissions and settings — all from one place.</p>
                </div>
                <p className="text-xs text-slate-400">© {new Date().getFullYear()} Gaurav Sankalp Phase-1 Association</p>
            </div>
            <div className="flex items-center justify-center p-6 sm:p-12">
                <form onSubmit={submit} className="w-full max-w-md surface-card p-8" data-testid="admin-login-form">
                    <div className="mb-6 flex items-center gap-3 lg:hidden">
                        <Logo size={40} />
                        <div className="font-serif text-lg font-semibold text-brand-blue">Gaurav Sankalp</div>
                    </div>
                    <div className="overline">Admin</div>
                    <h2 className="mt-2 font-serif text-3xl text-slate-900">Sign in</h2>
                    <p className="mt-1 text-sm text-slate-500">Enter your credentials to continue.</p>
                    <div className="mt-6 space-y-4">
                        <label className="block">
                            <span className="text-xs font-medium uppercase tracking-wider text-slate-500">Username</span>
                            <div className="relative mt-1">
                                <UserIcon size={16} className="absolute left-3 top-3 text-slate-400" />
                                <input
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    data-testid="admin-username"
                                    className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-4 text-sm outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20"
                                />
                            </div>
                        </label>
                        <label className="block">
                            <span className="text-xs font-medium uppercase tracking-wider text-slate-500">Password</span>
                            <div className="relative mt-1">
                                <Lock size={16} className="absolute left-3 top-3 text-slate-400" />
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    data-testid="admin-password"
                                    className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-4 text-sm outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20"
                                />
                            </div>
                        </label>
                    </div>
                    <button type="submit" disabled={busy} className="btn-primary mt-6 w-full" data-testid="admin-login-submit">
                        {busy ? "Signing in…" : "Sign in"}
                    </button>
                    <p className="mt-6 text-center text-xs text-slate-400">Access restricted to authorised administrators only.</p>
                </form>
            </div>
        </div>
    );
}
